import { Form } from 'antd';
import Box from 'components/Box';
import Text from 'components/Text';
import styled from 'styled-components';
import MyRadio from '../../PaymentTabMenu/components/MyRadio';
import SwitchV2 from 'components/SwitchV2';
import IcDoubleReceipt from 'assets/Icons/IcDoubleReceipt';
import IcStaff from 'assets/Icons/IcStaff';
import IcReceipt from 'assets/Icons/IcReceipt';
import useGetReceiptPrintConfig from 'features/settingService/services/hooks/useGetReceiptPrintConfig';
import { useEffect, useState } from 'react';
import Button from 'components/Button';
import { getMessageError, IResponseDataBody } from 'services/response';
import settingApis from 'features/settingService/services/apis';
import toast from 'utils/toast';
const labelReceiptOptions = [
  { value: 'Cash', label: 'Cash' },
  { value: 'Bonus', label: 'Bonus' },
];

interface IReceiptFormValues {
  labelReportReceipt: string;
  enableDoubleReceipt?: boolean;
  enableReceiptStaff?: boolean;
  enablePrintReceiptOwnerWhenNoReceipt?: boolean;
}

const Receipt = () => {
  const { data, mutate } = useGetReceiptPrintConfig();
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm<IReceiptFormValues>();

  useEffect(() => {
    if (!data) return;

    form.setFieldsValue({
      labelReportReceipt: data.labelReportReceipt || 'CASH',
      enableDoubleReceipt: data.enableDoubleReceipt,
      enableReceiptStaff: data.enableReceiptStaff,
      enablePrintReceiptOwnerWhenNoReceipt: data.enablePrintReceiptOwnerWhenNoReceipt,
    });
  }, [data]);

  const handleSubmit = async (values: IReceiptFormValues) => {
    setLoading(true);
    try {
      const res: IResponseDataBody = await settingApis.updateReceiptPrintConfig({
        labelReportReceipt: values.labelReportReceipt,
        enableDoubleReceipt: values.enableDoubleReceipt,
        enableReceiptStaff: values.enableReceiptStaff,
        enablePrintReceiptOwnerWhenNoReceipt: values.enablePrintReceiptOwnerWhenNoReceipt,
      });

      if (res?.data?.data) {
        toast.success('Update receipt print config successfully');
        mutate();
      }
    } catch (error) {
      toast.error(getMessageError(error) || 'Update receipt print config failed');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    if (data) {
      form.setFieldsValue({
        labelReportReceipt: data.labelReportReceipt || 'CASH',
        enableDoubleReceipt: data.enableDoubleReceipt,
        enableReceiptStaff: data.enableReceiptStaff,
        enablePrintReceiptOwnerWhenNoReceipt: data.enablePrintReceiptOwnerWhenNoReceipt,
      });
    }
  };

  return (
    <Container>
      <Form form={form} layout='vertical' className='receipt-form' onFinish={handleSubmit}>
        <LabelReportReceipt>
          <Text variant='H5'>Label cash/bonus report receipt</Text>
          <Form.Item noStyle name='labelReportReceipt'>
            <LabelReportReceiptOptions />
          </Form.Item>
        </LabelReportReceipt>
        <PrintOptions>
          <Text variant='H5'>Print options</Text>
          <Box width='100%' display='flex' gap='4'>
            <PrintItem>
              <Box display='flex' alignItems='center' gap='4'>
                <BoxIcon>
                  <IcDoubleReceipt width={48} height={48} fill='#2D6772' />
                </BoxIcon>
                <Text variant='H4' style={{ fontWeight: 500 }}>
                  Print double receipt
                </Text>
              </Box>
              <Form.Item name='enableDoubleReceipt' valuePropName='checked' noStyle>
                <SwitchV2 />
              </Form.Item>
            </PrintItem>
            <PrintItem>
              <Box display='flex' alignItems='center' gap='4'>
                <BoxIcon>
                  <IcStaff width={48} height={48} fill='#2D6772' />
                </BoxIcon>
                <Text variant='H4' style={{ fontWeight: 500 }}>
                  Print receipt for staff
                </Text>
              </Box>
              <Form.Item name='enableReceiptStaff' valuePropName='checked' noStyle>
                <SwitchV2 />
              </Form.Item>
            </PrintItem>
            <PrintItem>
              <Box display='flex' alignItems='center' gap='4'>
                <BoxIcon>
                  <IcReceipt width={48} height={48} fill='#2D6772' />
                </BoxIcon>
                <Text variant='H4' style={{ fontWeight: 500 }} wrapWidth='300px'>
                  Print receipt even when &quot;No Receipt&quot; is selected
                </Text>
              </Box>

              <Form.Item name='enablePrintReceiptOwnerWhenNoReceipt' valuePropName='checked' noStyle>
                <SwitchV2 size='default'/>
              </Form.Item>
            </PrintItem>
          </Box>
        </PrintOptions>
      </Form>
      <BottomAction>
        <Button ntype='SECONDARY' cardCashier htmlType='button' width='10rem' onClick={handleReset} disabled={loading}>
          Reset
        </Button>
        <Button ntype='PRIMARY' cardCashier htmlType='button' width='10rem' onClick={() => form.submit()} disabled={loading}>
          Save
        </Button>
      </BottomAction>
    </Container>
  );
};

export default Receipt;

const Container = styled.div`
  width: 100%;
  height: 100%;

  .receipt-form {
    width: 100%;
    height: calc(100% - 90px);
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 24px;
    background: #fff;
    font-family: Poppins;
  }
`;

const LabelReportReceipt = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  border-radius: 8px;
  border: 1px solid #ccd4dc;
  background: #f6f7fc;
  padding: 24px;
`;

const PrintOptions = styled.div`
  display: flex;
  padding: 24px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  border-radius: 8px;
  border: 1px solid #ccd4dc;
  background: #f6f7fc;
`;

const PrintItem = styled.div`
  display: flex;
  padding: 24px;
  justify-content: space-between;
  align-items: center;
  flex: 1 0 0;
  background: #fff;
  border-radius: 4px;

  .ant-switch {
    zoom: 1.6;
  }
`;

const BoxIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 100px;
  background: #c6e9ef;
`;

const BottomAction = styled.div`
  position: sticky;
  bottom: 0;
  z-index: 10;
  padding: 16px 24px;
  display: flex;
  justify-content: flex-end;
  gap: 24px;
  background: #f1f5f9;
  box-shadow: 0 -4px 8px 0 rgba(0, 0, 0, 0.15);

  .CONTENT_2 {
    font-size: 20px;
    font-weight: 600;
    font-family: Poppins;
  }
`;

interface ILabelReportReceiptOptions {
  value?: string;
  onChange?: (value: string) => void;
}

const LabelReportReceiptOptions = ({ value, onChange }: ILabelReportReceiptOptions) => {
  return (
    <Box display='flex' gap='3'>
      {labelReceiptOptions.map((opt) => (
        <MyRadio
          key={opt.value}
          value={value === opt.value}
          label={opt.label}
          onChange={() => onChange && onChange(opt.value)}
          style={{ flex: 1, padding: 16, borderRadius: 8, background: '#fff' }}
        />
      ))}
    </Box>
  );
};
